import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, MapPin, Heart, MessageCircle, Send } from "lucide-react";
import { FadeUp, MaskedLines } from "./Motion";
import { scrollToId } from "../../lib/api";
import { trackCta } from "../../lib/analytics";

function CardShell({ className = "", children, style }) {
  return (
    <motion.div style={style} className={`absolute aspect-[9/16] w-[46%] overflow-hidden rounded-[22px] border border-white/10 bg-panel shadow-[0_40px_80px_-30px_rgba(0,0,0,0.9)] ${className}`}>
      {children}
    </motion.div>
  );
}

function HeroStack() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={reset} className="relative mx-auto aspect-[1/1.05] w-full max-w-[520px] [perspective:1400px]" data-testid="hero-visual">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative h-full w-full">
        <CardShell className="left-[2%] top-[10%] p-4 font-mono text-[10px] leading-relaxed text-slate-300" style={{ transform: "translateZ(-80px) rotate(-8deg)" }}>
          <p className="mb-3 text-[9px] uppercase tracking-[0.2em] text-cyan-300">01 · Script</p>
          <p className="text-electric">HOOK</p><p className="mb-2 text-white">"Thinking about selling your home?"</p>
          <p className="text-electric">PROBLEM</p><p className="mb-2">"Pricing it right isn't easy."</p>
          <p className="text-electric">SOLUTION</p><p className="mb-2">"Let a local expert guide you."</p>
          <p className="text-electric">CTA</p><p>"Get your free home valuation."<span className="ml-0.5 inline-block h-3 w-1.5 translate-y-0.5 animate-blink bg-cyan-300" /></p>
        </CardShell>
        <CardShell className="left-[27%] top-[4%]" style={{ transform: "translateZ(0px) rotate(-1deg)" }}>
          <img src="/images/re-agent-sign.jpg" alt="Example: realtor filming an ad in front of a home for sale" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/50" />
          <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-black/60 px-2 py-1 font-mono text-[10px] text-white">
            <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" /> REC 00:12
          </div>
          <span className="absolute left-4 top-12 h-5 w-5 border-l-2 border-t-2 border-white/70" />
          <span className="absolute bottom-10 right-4 h-5 w-5 border-b-2 border-r-2 border-white/70" />
          <p className="absolute bottom-3 left-3 font-mono text-[9px] uppercase tracking-[0.2em] text-white/80">02 · Film</p>
        </CardShell>
        <CardShell className="right-[0%] top-[16%] bg-white text-slate-900" style={{ transform: "translateZ(90px) rotate(6deg)" }}>
          <div className="flex items-center gap-2 px-3 py-2.5">
            <div className="h-6 w-6 rounded-full bg-gradient-to-br from-electric to-cyan-300" />
            <div className="leading-tight"><p className="text-[10px] font-semibold">Summit Realty Group</p><p className="text-[8px] text-slate-500">Sponsored</p></div>
          </div>
          <p className="px-3 pb-2 text-[10px] leading-snug">Thinking about selling? Get your free home valuation today.</p>
          <div className="relative h-[52%]"><img src="/images/re-house.jpg" alt="" className="h-full w-full object-cover" /></div>
          <div className="flex items-center justify-between bg-slate-100 px-3 py-2">
            <p className="text-[9px] font-semibold">Free Valuation</p>
            <span className="rounded bg-slate-300 px-2 py-1 text-[9px] font-semibold">Learn More</span>
          </div>
          <div className="flex gap-3 px-3 py-2 text-slate-500"><Heart className="h-3.5 w-3.5" /><MessageCircle className="h-3.5 w-3.5" /><Send className="h-3.5 w-3.5" /></div>
        </CardShell>
        <span className="absolute -top-2 right-[4%] rounded-full border border-amber-300/30 bg-ink/80 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-amber-200" style={{ transform: "translateZ(120px)" }}>Example · Realtor ad</span>
        <motion.div style={{ transform: "translateZ(150px)" }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6, duration: 0.8 }}
          className="absolute bottom-[6%] left-[8%] flex items-center gap-3 rounded-2xl glass bg-ink/70 px-4 py-3 shadow-2xl">
          <span className="relative grid h-8 w-8 place-items-center rounded-full bg-electric">
            <span className="absolute inset-0 animate-ping2 rounded-full bg-electric" />
            <span className="relative text-xs font-bold">+1</span>
          </span>
          <div className="leading-tight"><p className="text-xs font-semibold text-white">New Lead Received</p><p className="text-[11px] text-slate-400">Home Valuation · just now</p></div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const yVis = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section id="top" ref={ref} className="noise relative isolate overflow-hidden pt-28 pb-16 sm:pt-36 lg:min-h-[100svh] lg:pb-24" data-testid="hero-section">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 grid-lines [mask-image:radial-gradient(ellipse_at_30%_20%,#000_20%,transparent_70%)]" />
        <div className="absolute -left-40 -top-40 h-[620px] w-[620px] animate-drift rounded-full bg-electric/25 blur-[140px]" />
        <div className="absolute right-[-10%] top-[30%] h-[520px] w-[520px] animate-drift rounded-full bg-cyan-400/10 blur-[140px] [animation-delay:-6s]" />
      </div>

      <div className="container-x grid items-center gap-14 lg:grid-cols-[1.25fr_1fr]">
        <motion.div style={{ y: yText }}>
          <FadeUp><p className="eyebrow mb-7 flex items-center gap-3"><span className="h-px w-10 bg-cyan-300/60" />Meta Ads · Billings, MT</p></FadeUp>
          <MaskedLines
            lines={[
              <>Fully <span className="font-serif italic font-normal text-gradient pr-2">Done-For-You</span></>,
              "Facebook & Instagram",
              "Ads.",
            ]}
            className="text-[2.65rem] leading-[0.98] sm:text-6xl lg:text-[5.4rem] font-semibold tracking-[-0.045em] text-white"
          />
          <FadeUp delay={0.5}>
            <p className="mt-8 max-w-xl text-lg sm:text-xl font-medium text-white">
              We script it. We film it. We edit it. We run your ads. <span className="text-cyan-300">You get the leads.</span>
            </p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-400">
              From creating professional advertisements to managing your campaigns and automating your follow-up, we handle the entire process.
            </p>
          </FadeUp>
          <FadeUp delay={0.65} className="mt-10 flex flex-wrap gap-3">
            <Link to="/get-started" data-testid="hero-get-leads-btn" onClick={() => trackCta("Get More Leads", "hero")} className="btn-primary group">
              Get More Leads <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <button data-testid="hero-see-results-btn" onClick={() => { trackCta("See Our Results", "hero"); scrollToId("results"); }} className="btn-ghost group">
              See Our Results <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </FadeUp>
          <FadeUp delay={0.8}>
            <p className="mt-10 flex max-w-md items-start gap-2 text-sm text-slate-400">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />
              Based in Billings, Montana. Helping service-based businesses grow through Meta advertising.
            </p>
          </FadeUp>
        </motion.div>
        <motion.div style={{ y: yVis }} initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}>
          <HeroStack />
        </motion.div>
      </div>
    </section>
  );
}
